import { LinkInterface } from '@teko/types/ds';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { MenuItem, MenuItemBanner, PageTypeEnum } from '@teko/discovery';
import { buildSlugUrl } from '.';
import slug from '@teko/slug';

const convertUrl = (
  targetPath: string,
  urlType: string,
  isTargetBlank?: boolean,
): LinkInterface | undefined => {
  if (!targetPath) {
    return undefined;
  }
  if (urlType === 'rewrite') {
    return {
      href: '/search?router=productListing&' + targetPath,
      target: isTargetBlank ? '_blank' : '_self',
    };
  }
  return {
    href: targetPath,
    target: isTargetBlank ? '_blank' : '_self',
  };
};

const getMenuItemUrl = (item: MenuItem): LinkInterface => {
  const { isTargetBlank, url, targetPath, urlType } = item || {};
  const target = isTargetBlank ? '_blank' : '_self';
  const href =
    url ||
    (urlType === 'rewrite'
      ? '/search?router=productListing&' + targetPath
      : targetPath);
  return {
    href,
    target,
  };
};

const getUrl = (item: MenuItem): LinkInterface | null => {
  const { isTargetBlank, url } = item || {};
  const target = isTargetBlank ? '_blank' : '_self';
  if (!url) return null;
  return {
    href: url,
    target,
  };
};

const convertUrlParamsToObject = (urlParams: URLSearchParams) => {
  const urlParamsArray = Array.from(urlParams);
  return urlParamsArray.reduce(
    (acc, cur) => ({
      ...acc,
      [cur[0]]: cur[1],
    }),
    {},
  );
};

const computeBlockPageUrl = (menu: MenuItem): LinkInterface | null => {
  if (!menu.targetPath || menu.urlType !== 'rewrite') {
    return null;
  }
  const menuConfig = menu.configs;
  const targetFilterParams = convertUrlParamsToObject(
    new URLSearchParams(menu.targetPath),
  );
  // targetPath has only 1 filter config
  if (Object.keys(targetFilterParams)?.length === 1) {
    const filterKey = Object.keys(targetFilterParams)[0];
    const filterValue =
      menuConfig[filterKey]?.length === 1 ? menuConfig[filterKey][0] : null;
    if (!filterValue) {
      return null;
    }
    switch (filterKey) {
      case 'brands':
        return {
          href: {
            pathname: '/brands/[brandId]',
            query: {
              brandId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: {
            pathname: buildSlugUrl(PageTypeEnum.BRAND, menu.name, filterValue),
          },
          shallow: true,
        };
      case 'masterCategoryIds':
        return {
          href: {
            pathname: '/block-categories/[categoryId]',
            query: {
              categoryId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as:
            buildSlugUrl(PageTypeEnum.CATEGORY, menu.name, filterValue) ||
            undefined,
          shallow: true,
        };
      case 'sellerIds':
        return {
          href: {
            pathname: '/sellers/[sellerId]',
            query: {
              sellerId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as:
            buildSlugUrl(PageTypeEnum.SELLER, menu.name, filterValue) ||
            undefined,
          shallow: true,
        };
      case 'categories':
        return {
          href: {
            pathname: '/seller-categories/[sellerCategoryId]',
            query: {
              sellerCategoryId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: `/${slug(menu.name)}-scat.${filterValue}`,
          shallow: true,
        };
      default:
        return null;
    }
  }
  return null;
};

export const convertMegaMenuItem = (item: MenuItem): MegaMenuItemInterface => {
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    link: getUrl(item) || computeBlockPageUrl(item) || getMenuItemUrl(item),
    banner: !!item.banners?.length
      ? {
          src: item.banners[0]?.image,
          hoverEffect: 'zoom',
          link: convertUrl(
            item.banners[0]?.targetPath,
            item.banners[0]?.urlType,
          ),
        }
      : undefined,
    children: item.children?.map(convertMegaMenuItem) || [],
  };
};

const getValidBanners = (banners: MenuItemBanner[]) => {
  return banners.filter((banner) => {
    if (!banner.isActive) return false;
    const currentTime = new Date().getTime();

    if (banner?.startDate) {
      if (!banner?.endDate) {
        return currentTime >= banner.startDate * 1000;
      }
      return (
        banner.startDate * 1000 <= currentTime &&
        banner.endDate * 1000 >= currentTime
      );
    }
    if (banner?.endDate) {
      return currentTime <= banner.endDate * 1000;
    }
    return true;
  });
};

export const buildTreeCategories = (menuItems: MenuItem[]): MenuItem[] => {
  const rootMenuItems = menuItems.filter(({ parentId }) => !parentId);
  const buildMenuItem = (root: MenuItem): MenuItem => {
    const children = menuItems.filter(({ parentId }) => parentId === root.id);
    return {
      ...root,
      banners: getValidBanners(root.banners || []),
      children: children.map(buildMenuItem),
    };
  };
  return rootMenuItems.map((root) => buildMenuItem(root));
};
